import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { App, hasActivatedProduct } from 'app/core/app/app.model';
import {
  isProductSection,
  projectSectionPath,
} from 'app/core/app/project-section-path';
import { AppService } from 'app/core/app/app.service';
import { RECENT_PROJECTS_LIMIT } from 'app/core/app/recent-projects';
import {
  BillingStateBadge,
  projectBillingStateBadge,
} from 'app/core/billing/billing-state-badge';
import { ProjectCreationService } from 'app/core/projects/project-creation.service';
import {
  acceptsInlineSuggestion,
  inlineSuggestion,
} from 'app/core/search/inline-suggestion';
import { rankMatches } from 'app/core/search/rank-matches';
import { FleetBadgeComponent } from 'app/modules/admin/fleet/fleet-badge/fleet-badge.component';
import {
  ProductFilter,
  matchesProductFilter,
  productFilters,
} from 'app/core/app/project-products';
import {
  HighlightPart,
  containsAllTerms,
  highlightParts,
} from './app-selector.utils';

/** One row of the list, with everything the template needs precomputed. */
interface ProjectOption {
  app: App;
  /** Unique per row: a recent project is listed twice. */
  optionId: string;
  name: HighlightPart[];
  /** Set when the search matched the id rather than the name. */
  idMatch: HighlightPart[] | null;
  badge: BillingStateBadge | null;
}

interface ProjectGroup {
  label: string | null;
  options: ProjectOption[];
}

/**
 * The header's project switcher.
 *
 * A fixed-height panel rather than a `mat-menu`: with a few dozen projects the
 * menu grew to the full window height. Search, filters and footer stay put and
 * only the list scrolls; entries are one line, and the long project ids are
 * shown only where a search matched them.
 *
 * Focus stays in the search box the whole time and the arrow keys move a
 * highlight (`aria-activedescendant`), which is the combobox pattern — the menu
 * needed a pile of workarounds to let an input live inside it at all.
 */
@Component({
  selector: 'app-selector',
  templateUrl: './app-selector.component.html',
  styleUrls: ['./app-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    FleetBadgeComponent,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
  ],
})
export class AppSelectorComponent implements OnInit {
  private readonly _appService = inject(AppService);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _projectCreation = inject(ProjectCreationService);
  private readonly _router = inject(Router);

  @ViewChild('trigger', { read: ElementRef })
  private _trigger?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') private _searchInput?: ElementRef<HTMLInputElement>;

  /** The trigger's badge, including Fleet whitelist status. */
  readonly badgeFor = projectBillingStateBadge;

  readonly canCreate = this._projectCreation.canCreate;

  /** Below the trigger, left-aligned; right-aligned where that does not fit. */
  readonly positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
  ];

  apps: App[] = [];
  activeApp: App | null = null;
  isOpen = false;

  /** What the user has typed into the search box. */
  query = '';
  /** `all` or a product slug, as on the projects page. */
  productFilter = 'all';

  groups: ProjectGroup[] = [];
  /** The rows of {@link groups} in display order, for keyboard navigation. */
  options: ProjectOption[] = [];
  highlighted = 0;

  /** Product tabs with counts; empty when filtering would change nothing. */
  productFilters: ProductFilter[] = [];

  private _recentIds: string[] = [];

  ngOnInit(): void {
    combineLatest([
      this._appService.apps$,
      this._appService.activeApp$,
      this._appService.recentAppIds$,
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([apps, activeApp, recentIds]) => {
        this.apps = apps ?? [];
        this.activeApp = activeApp;
        this._recentIds = recentIds;
        this._rebuild();
        this._changeDetectorRef.markForCheck();
      });
  }

  get highlightedOption(): ProjectOption | null {
    return this.options[this.highlighted] ?? null;
  }

  /**
   * The first project whose name begins with what has been typed, completed
   * inline behind the input. Only a prefix can line up with the typed text.
   */
  get suggestion(): string {
    return inlineSuggestion(
      this.query,
      this.options.map((option) => option.app.name),
    );
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.query = '';
    this.productFilter = 'all';
    this.isOpen = true;
    this._rebuild();
  }

  close({ restoreFocus = false } = {}): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._changeDetectorRef.markForCheck();
    if (restoreFocus) this._trigger?.nativeElement.focus();
  }

  /** The overlay is in the DOM: hand focus to the search box. */
  onAttached(): void {
    setTimeout(() => this._searchInput?.nativeElement.focus());
  }

  /** A click on the trigger itself is handled by `toggle`. */
  onOutsideClick(event: MouseEvent): void {
    if (!this._trigger?.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  onQueryChange(): void {
    this._rebuild();
  }

  setProductFilter(value: string): void {
    this.productFilter = value;
    this._rebuild();
    this._searchInput?.nativeElement.focus();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.highlight(
          this.highlighted + (event.key === 'ArrowDown' ? 1 : -1),
          true,
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedOption) this.choose(this.highlightedOption.app);
        break;
      case 'Escape':
        event.preventDefault();
        this.close({ restoreFocus: true });
        break;
      case 'Tab':
      case 'ArrowRight':
        this._acceptSuggestion(event);
        break;
    }
  }

  highlight(index: number, scroll = false): void {
    if (!this.options.length) return;
    this.highlighted = (index + this.options.length) % this.options.length;
    if (scroll) {
      const id = this.options[this.highlighted].optionId;
      setTimeout(() =>
        document.getElementById(id)?.scrollIntoView({ block: 'nearest' }),
      );
    }
  }

  createProject(name?: string): void {
    this.close();
    this._projectCreation.start(name);
  }

  /**
   * Switch projects, staying on the same page where that page exists.
   *
   * Query parameters are deliberately not carried over: a filter, a search
   * term or a page number describes the project being left.
   */
  choose(app: App): void {
    this.close();
    this._appService.setActiveApp(app);
    this._router.navigate(['/projects', app.id, ...this._sectionFor(app)]);
  }

  /**
   * Tab (the convention for ghost text) or Right arrow at the end of the input
   * accepts the completion. Tab is swallowed only when there is something to
   * accept, so keyboard users are never trapped in the field.
   */
  private _acceptSuggestion(event: KeyboardEvent): void {
    const suggestion = this.suggestion;
    if (!acceptsInlineSuggestion(event, this.query, suggestion)) return;
    event.preventDefault();
    this.query = suggestion;
    this._rebuild();
  }

  /** Recompute groups, rows and filter counts from the current inputs. */
  private _rebuild(): void {
    const query = this.query.trim();
    const matches = rankMatches(this.apps, query, (app) => [app.name, app.id]);
    const ofProduct = (app: App) =>
      matchesProductFilter(app, this.productFilter);

    this.productFilters = productFilters(this.apps, matches);
    if (
      !this.productFilters.some((filter) => filter.value === this.productFilter)
    ) {
      this.productFilter = 'all';
    }

    const groups: ProjectGroup[] = [];
    if (!query) {
      const recent = this._recentIds
        .map((id) => this.apps.find((app) => app.id === id))
        .filter((app): app is App => !!app && ofProduct(app))
        .slice(0, RECENT_PROJECTS_LIMIT);
      if (recent.length) {
        groups.push({
          label: 'Recently used',
          options: recent.map((app) => this._option(app, 'recent', query)),
        });
      }
    }

    const all = matches
      .filter(ofProduct)
      .map((app) => this._option(app, 'all', query));
    if (all.length) {
      groups.push({
        label: groups.length ? 'All projects' : null,
        options: all,
      });
    }

    this.groups = groups;
    this.options = groups.flatMap((group) => group.options);
    this.highlighted = 0;
  }

  private _option(app: App, group: string, query: string): ProjectOption {
    const idMatch =
      !!query &&
      !containsAllTerms(app.name, query) &&
      containsAllTerms(app.id, query);
    return {
      app,
      optionId: `app-selector-${group}-${app.id}`,
      name: highlightParts(app.name, idMatch ? '' : query),
      idMatch: idMatch ? highlightParts(app.id, query) : null,
      badge: projectBillingStateBadge(app),
    };
  }

  /**
   * The current section, or nothing when the target project cannot show it —
   * a project switch should not land on another product's activation page.
   */
  private _sectionFor(app: App): string[] {
    const segments = projectSectionPath(this._router.routerState.snapshot.root);
    const section = segments[0];

    return isProductSection(section) && !hasActivatedProduct(app, section)
      ? []
      : segments;
  }
}
